import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../../config/axiosConfig';
import { SIGNUP_URLS, TIMEZONES } from '../../config/Constants';
import { showErrorMsg } from '../../config/common';
import TermsAndConditionsModal from '../../components/modals/TermsAndConditionsModal';
import PrivacyPolicyModal from '../../components/modals/PrivacyPolicyModal';
import SignupSuccessModal from '../../components/modals/SignupSuccessModal';

// Zod schema for form validation
const createSignupSchema = (billingEnabled: boolean) =>
  z
    .object({
      // Team info
      teamName: z.string().min(1, 'School/Team name is required').trim(),
      address: z.string().min(1, 'Address is required').trim(),
      city: z.string().optional(),
      state: z.string().optional(),
      zip: z.string().optional(),
      // User info
      firstName: z.string().min(1, 'First name is required').trim(),
      lastName: z.string().min(1, 'Last name is required').trim(),
      email: z.string().email('Invalid email address').trim(),
      phone: z.string().min(1, 'Phone number is required').trim(),
      timezone: z.string().min(1, 'Timezone is required'),
      role: z.string().min(1, 'Role is required'),
      password: z.string().min(6, 'Password must be at least 6 characters'),
      confirmPassword: z.string().min(6, 'Password confirmation is required'),
      acknowledgeUploadLimit: z.boolean().optional(),
      acceptTerms: z.boolean().refine((val) => val === true, {
        message: 'You must accept the Terms & Conditions',
      }),
    })
    .superRefine((data, ctx) => {
      if (billingEnabled && data.acknowledgeUploadLimit !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['acknowledgeUploadLimit'],
          message: 'You must acknowledge the game upload and subscription terms',
        });
      }
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    });

type SignupFormData = z.infer<ReturnType<typeof createSignupSchema>>;

interface Role {
  _id: string;
  label: string;
  value: string;
}

const STEPS = [
  { id: 1, title: 'Team Info', description: 'Your team details' },
  { id: 2, title: 'Your Account', description: 'Personal information' },
  { id: 3, title: 'Security', description: 'Set your password' },
];

const Signup: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [billingEnabled, setBillingEnabled] = useState(true);
  const [submittedData, setSubmittedData] = useState<{
    email: string;
    teamName: string;
  } | null>(null);
  const signupSchema = useMemo(() => createSignupSchema(billingEnabled), [billingEnabled]);

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      teamName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      timezone: 'eastern',
      role: '',
      password: '',
      confirmPassword: '',
      acknowledgeUploadLimit: false,
      acceptTerms: false,
    },
    mode: 'onChange',
  });

  // Fetch available roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await api.get(SIGNUP_URLS.ROLES);
        if (res.data.success) {
          setRoles(res.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch roles:', error);
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchSignupConfig = async () => {
      try {
        const res = await api.get(SIGNUP_URLS.CONFIG);
        setBillingEnabled(!!res.data?.data?.billingEnabled);
      } catch (error) {
        console.error('Failed to fetch signup config:', error);
        setBillingEnabled(true);
      }
    };

    fetchSignupConfig();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);

    // Create preview
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedImage(null);
    }
  };

  const validateStep = async (step: number): Promise<boolean> => {
    let fieldsToValidate: (keyof SignupFormData)[] = [];

    switch (step) {
      case 1:
        fieldsToValidate = ['teamName', 'address'];
        break;
      case 2:
        fieldsToValidate = ['firstName', 'lastName', 'email', 'phone', 'timezone', 'role'];
        break;
      case 3:
        fieldsToValidate = billingEnabled
          ? ['password', 'confirmPassword', 'acknowledgeUploadLimit', 'acceptTerms']
          : ['password', 'confirmPassword', 'acceptTerms'];
        break;
    }

    const result = await trigger(fieldsToValidate);
    return result;
  };

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const onSubmit = async (data: SignupFormData) => {
    try {
      let res;

      // If logo is selected, use FormData; otherwise use JSON
      if (logoFile) {
        const formData = new FormData();
        formData.append('image', logoFile);
        formData.append('teamName', data.teamName);
        formData.append('address', data.address);
        if (data.city) formData.append('city', data.city);
        if (data.state) formData.append('state', data.state);
        if (data.zip) formData.append('zip', data.zip);
        formData.append('firstName', data.firstName);
        formData.append('lastName', data.lastName);
        formData.append('email', data.email);
        formData.append('phone', data.phone);
        formData.append('timezone', data.timezone);
        formData.append('role', data.role);
        formData.append('password', data.password);
        formData.append('acceptTerms', String(data.acceptTerms));

        res = await api.post(SIGNUP_URLS.SIGNUP, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        res = await api.post(SIGNUP_URLS.SIGNUP, {
          teamName: data.teamName,
          address: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          timezone: data.timezone,
          role: data.role,
          password: data.password,
          acceptTerms: data.acceptTerms,
        });
      }

      if (res.data.success) {
        // Store the submitted data for the success modal
        setSubmittedData({
          email: data.email,
          teamName: data.teamName,
        });
        setShowSuccessModal(true);
      } else {
        showErrorMsg(res.data.message || 'Failed to create account');
      }
    } catch (error: any) {
      showErrorMsg(error.response?.data?.message || 'Failed to create account');
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-6">
      <div className="flex items-center justify-center">
        {STEPS.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all duration-300 ${
                  currentStep >= step.id ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {currentStep > step.id ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  step.id
                )}
              </div>
              <div className="mt-2 text-center text-nowrap">
                <p className={`text-sm font-medium ${currentStep >= step.id ? 'text-green-600' : 'text-gray-500'}`}>
                  {step.title}
                </p>
                <p className="text-xs text-gray-400 hidden sm:block">{step.description}</p>
              </div>
            </div>
            {index < STEPS.length - 1 && (
              <div
                className={`w-16 sm:w-24 h-1 mx-2 rounded transition-all duration-300 ${
                  currentStep > step.id ? 'bg-green-600' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-2 animate-fadeIn">
      {/* <div className='text-center mb-6'>
        <div className='inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4'>
          <svg
            className='w-8 h-8 text-green-600'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
            />
          </svg>
        </div>
        <h3 className='text-xl font-bold text-gray-900'>
          Tell us about your team
        </h3>
        <p className='text-gray-500 mt-1'>
          Enter your team or organization details
        </p>
      </div> */}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          School/Team Name <span className="text-red-500">*</span>
        </label>
        <Controller
          name="teamName"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                errors.teamName ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
            />
          )}
        />
        {errors.teamName && <p className="text-red-500 text-xs mt-1">{errors.teamName.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address <span className="text-red-500">*</span>
        </label>
        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                errors.address ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
            />
          )}
        />
        {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address.message}</p>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="col-span-2 sm:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                placeholder="City"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
              />
            )}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                placeholder="State"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
              />
            )}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
          <Controller
            name="zip"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                placeholder="ZIP"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
              />
            )}
          />
        </div>
      </div>

      {/* Logo Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
        <div className="space-y-3">
          {selectedImage && (
            <div className="relative inline-block">
              <img
                src={selectedImage}
                alt="Logo preview"
                className="h-20 w-20 object-contain border border-gray-300 rounded-lg"
              />
              <button
                type="button"
                onClick={() => {
                  setLogoFile(null);
                  setSelectedImage(null);
                }}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
              >
                ×
              </button>
            </div>
          )}
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
            />
            <p className="text-xs text-gray-500 mt-1">Upload your team logo (max 5MB)</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5 animate-fadeIn">
      {/* <div className='text-center mb-6'>
        <div className='inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4'>
          <svg
            className='w-8 h-8 text-blue-600'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
            />
          </svg>
        </div>
        <h3 className='text-xl font-bold text-gray-900'>Create your account</h3>
        <p className='text-gray-500 mt-1'>Enter your personal information</p>
      </div> */}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                  errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              />
            )}
          />
          {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Last Name <span className="text-red-500">*</span>
          </label>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                  errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              />
            )}
          />
          {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email Address <span className="text-red-500">*</span>
        </label>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="email"
              placeholder="you@example.com"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
            />
          )}
        />
        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="tel"
              placeholder="(555) 555-5555"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                errors.phone ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
            />
          )}
        />
        {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="relative overflow-visible">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Time Zone <span className="text-red-500">*</span>
          </label>
          <Controller
            name="timezone"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                  errors.timezone ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.timezone && <p className="text-red-500 text-xs mt-1">{errors.timezone.message}</p>}
        </div>

        <div className="relative overflow-visible">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Your Role <span className="text-red-500">*</span>
          </label>
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                disabled={loadingRoles}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                  errors.role ? 'border-red-500 bg-red-50' : 'border-gray-300'
                } ${loadingRoles ? 'bg-gray-100' : ''}`}
              >
                <option value="">Select your role</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.label}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role.message}</p>}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5 animate-fadeIn">
      {/* <div className='text-center mb-6'>
        <div className='inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4'>
          <svg
            className='w-8 h-8 text-amber-600'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
            />
          </svg>
        </div>
        <h3 className='text-xl font-bold text-gray-900'>Secure your account</h3>
        <p className='text-gray-500 mt-1'>Create a strong password</p>
      </div> */}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Password <span className="text-red-500">*</span>
        </label>
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <div className="relative">
              <input
                {...field}
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                className={`w-full px-4 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                  errors.password ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.956 9.956 0 012.01-3.368m2.148-2.149A9.956 9.956 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.955 9.955 0 01-4.043 5.058M15 12a3 3 0 11-6 0 3 3 0 016 0zm6 9L3 3"
                    />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.522 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S3.732 16.057 2.458 12z"
                    />
                  </svg>
                )}
              </button>
            </div>
          )}
        />
        {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
        <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Confirm Password <span className="text-red-500">*</span>
        </label>
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field }) => (
            <div className="relative">
              <input
                {...field}
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="••••••••"
                className={`w-full px-4 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                  errors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
              >
                {showConfirmPassword ? (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.956 9.956 0 012.01-3.368m2.148-2.149A9.956 9.956 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.955 9.955 0 01-4.043 5.058M15 12a3 3 0 11-6 0 3 3 0 016 0zm6 9L3 3"
                    />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.522 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S3.732 16.057 2.458 12z"
                    />
                  </svg>
                )}
              </button>
            </div>
          )}
        />
        {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>}
      </div>

      <div className="bg-gray-50 rounded-lg p-4 mt-6 space-y-4">
        {billingEnabled && (
          <>
            <div className="flex items-start gap-3">
              <Controller
                name="acknowledgeUploadLimit"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <input
                    id="upload-limit"
                    type="checkbox"
                    checked={!!value}
                    onChange={onChange}
                    className={`mt-1 h-5 w-5 flex-shrink-0 text-green-600 focus:ring-green-500 rounded border-gray-300 ${
                      errors.acknowledgeUploadLimit ? 'border-red-500' : ''
                    }`}
                  />
                )}
              />
              <label htmlFor="upload-limit" className="text-gray-700 text-sm leading-relaxed">
                I understand that my free trial allows me to add up to <strong>three games/scrimmages.</strong> To add
                more games to STATDOG, I agree to subscribe through the app&apos;s{' '}
                <strong>Billing &amp; Renewals</strong> page.
              </label>
            </div>
            {errors.acknowledgeUploadLimit && (
              <p className="text-red-500 text-xs mt-2">{errors.acknowledgeUploadLimit.message}</p>
            )}
          </>
        )}

        <div className="flex items-start gap-3">
          <Controller
            name="acceptTerms"
            control={control}
            render={({ field: { value, onChange } }) => (
              <input
                id="terms"
                type="checkbox"
                checked={value}
                onChange={onChange}
                className={`mt-1 h-5 w-5 flex-shrink-0 text-green-600 focus:ring-green-500 rounded border-gray-300 ${
                  errors.acceptTerms ? 'border-red-500' : ''
                }`}
              />
            )}
          />
          <label htmlFor="terms" className="text-gray-700 text-sm leading-relaxed">
            I agree to the{' '}
            <button
              type="button"
              onClick={() => setShowTermsModal(true)}
              className="text-green-600 hover:text-green-700 underline font-medium"
            >
              Terms & Conditions
            </button>{' '}
            and{' '}
            <button
              type="button"
              onClick={() => setShowPrivacyModal(true)}
              className="text-green-600 hover:text-green-700 underline font-medium"
            >
              Privacy Policy
            </button>
            .
          </label>
        </div>
        {errors.acceptTerms && <p className="text-red-500 text-xs mt-2">{errors.acceptTerms.message}</p>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-8">
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateX(10px); }
            to { opacity: 1; transform: translateX(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out;
          }
        `}
      </style>

      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <img src="/images/logo-stacked.png" alt="STATDOG Logo" className="h-32 w-auto" />
        </div>

        {/* Signup Card */}
        <div className="sm:bg-white sm:shadow-lg sm:rounded-2xl sm:border border-gray-100 overflow-visible py-8 px-4 sm:px-10">
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-extrabold text-gray-900">Welcome to STATDOG!</h2>
          </div>

          {renderStepIndicator()}

          <form
            className="overflow-visible"
            onSubmit={(e) => {
              // Always prevent default form submission
              // We handle submission explicitly via the submit button
              e.preventDefault();
            }}
          >
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}

            {/* Navigation Buttons */}
            <div className="mt-4 flex gap-3">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium"
                >
                  Back
                </button>
              )}

              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all font-semibold"
                >
                  Continue
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSubmitting || loadingRoles}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Creating Account...
                    </span>
                  ) : (
                    'Create Account'
                  )}
                </button>
              )}
            </div>
          </form>

          {/* Join existing team */}
          <div className="mt-4 text-center">
            <p className="text-gray-600 text-sm">
              Want to join your team in STATDOG? Please see your head coach to invite you or{' '}
              <a
                href="https://statdog.live/contact"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-700 font-semibold underline"
              >
                contact us
              </a>{' '}
              for assistance.
            </p>
          </div>

          {/* Login Link */}
          <div className="mt-4 text-center border-t pt-4">
            <p className="text-gray-600 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-green-600 hover:text-green-700 font-semibold">
                Sign In
              </Link>
            </p>
          </div>

          {/* Footer */}
          <div className="mt-4 pt-2 border-t text-center">
            <p className="text-xs text-gray-500">© 2026 Sports Data Partners, LLC dba STATDOG. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Modals */}
      <TermsAndConditionsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
      <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      <SignupSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        email={submittedData?.email || ''}
        teamName={submittedData?.teamName || ''}
      />
    </div>
  );
};

export default Signup;
